import { CloseIcon } from '@chakra-ui/icons'
import {
  Box,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react'
import { DateField, DateTimeField, getDateString } from '@hb/shared'
import { useMemo } from 'react'
import { DayPicker } from 'react-day-picker'
import { useInputSize } from '../Shared/sizes'

const DatePickerTrigger = ({
  value,
  onChange,
  disabled,
  readOnly,
  utc,
}: {
  value?: number
  onChange: (v: number | string | undefined) => void
  disabled?: boolean
  readOnly?: boolean
  utc?: boolean
}) => {
  const size = useInputSize()
  const isCompact = size === 'xs' || size === 'sm'

  return (
    <HStack
      spacing={2}
      px={isCompact ? 2 : 3}
      pl={isCompact ? 1 : 2}
      cursor="pointer"
      py={isCompact ? 1 : 2}
      background="white"
      boxShadow="md"
      width="150px"
      border={isCompact ? undefined : '1px solid #cdcdcd'}
      pointerEvents={disabled || readOnly ? 'none' : 'auto'}
      _hover={{ background: 'gray.50' }}
      borderRadius={6}
      color={value ? 'gray.700' : 'gray.500'}
      // style={style}
    >
      <Text
        // w='100%'
        fontFamily="hero-new"
        bg="transparent"
        flex={isCompact ? undefined : 1}
        variant="unstyled"
        display="flex"
        justifyContent="flex-start"
        // mx={1}
        fontSize="sm"
        // size='sm'
        px={2}
        py={isCompact ? 1 : 0}
        color="inherit"
        fontWeight="normal">
        {value ? getDateString(value, 'short', !utc) : 'Date'}
      </Text>
      {value ? (
        <IconButton
          ml="auto"
          size="xs"
          width="20px"
          minW="0"
          height="20px"
          bg="gray.100"
          borderRadius="full"
          color="white"
          aria-label="Clear"
          icon={<CloseIcon color="gray.500" w={2} h={2} />}
          onClick={e => {
            e.stopPropagation()
            onChange(undefined)
          }}
        />
      ) : null}
    </HStack>
  )
}

export const DatePicker = ({
  value,
  onChange,
  isOpen,
  onBlur,
  onFocus,
  name,
  date,
  utc,
  field,
  disabled,
  readOnly,
}: {
  value?: number
  onChange: (v: number | string | undefined) => void
  isOpen: boolean
  onFocus: () => void
  name: string
  onBlur: () => void
  date: Date | null
  utc?: boolean
  field: DateField | DateTimeField
  disabled?: boolean
  readOnly?: boolean
}) => {
  const { optional, minDate, isoFormat } = field
  const startMonth = useMemo(() => {
    if (!minDate) return undefined
    if (minDate === 'now') return new Date()
    return new Date(minDate)
  }, [minDate])
  const endMonth = useMemo(() => {
    if (!minDate) return undefined
    if (minDate === 'now') return new Date()
    return new Date(minDate)
  }, [minDate])

  if (disabled || readOnly) {
    return (
      <DatePickerTrigger
        value={value}
        onChange={onChange}
        disabled={disabled}
        readOnly={readOnly}
        utc={utc}
      />
    )
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onFocus}
      onClose={onBlur}
      trigger="click"
      strategy="fixed"
      closeDelay={100}
      placement="bottom">
      <HStack>
        <PopoverTrigger>
          <Box>
            <DatePickerTrigger
              value={value}
              onChange={onChange}
              disabled={disabled}
              readOnly={readOnly}
              utc={utc}
            />
          </Box>
        </PopoverTrigger>
      </HStack>
      <PopoverContent zIndex={2} onFocus={onFocus}>
        <PopoverArrow />
        <PopoverCloseButton />
        <HStack>
          <DayPicker
            required={!optional}
            // disabled={disabled}
            defaultMonth={date ?? undefined}
            // captionLayout="dropdown-buttons"
            startMonth={startMonth}
            endMonth={endMonth}
            styles={{
              caption: {
                fontFamily: 'hero-new',
                fontWeight: '400',
                color: '#555',
              },
              head: {
                fontFamily: 'hero-new',
              },
              nav: {
                fontFamily: 'hero-new',
              },
            }}
            modifiersStyles={{
              selected: {
                background: 'rgb(67, 173, 158)',
              },
            }}
            id={name}
            mode="single"
            selected={date ?? undefined}
            onMonthChange={updated => {
              if (!value) return
              const modified = new Date(
                updated.getTime() - (utc ? 0 : updated.getTimezoneOffset() * 60 * 1000),
              )
              modified.setUTCDate((date ?? new Date()).getUTCDate())
              onChange(isoFormat ? modified?.toISOString().split('T')[0] : modified?.getTime())
            }}
            onDayClick={updated => {
              if (isoFormat) {
                onChange(updated?.toISOString().split('T')[0])
              } else {
                onChange(
                  updated
                    ? updated.getTime() - (utc ? 0 : updated.getTimezoneOffset() * 60 * 1000)
                    : undefined,
                )
                onBlur()
              }
            }}
          />
        </HStack>
      </PopoverContent>
    </Popover>
  )
}
